import React, { useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import ProductCard from "../components/ProductCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Shop() {
  const { t, tr, lang, products, categories } = useApp();
  const { categorySlug, subSlug } = useParams();
  const [params] = useSearchParams();
  const query = params.get("q") || "";
  const [sort, setSort] = useState("newest");

  const category = categories.find((c) => c.slug === categorySlug);
  const sub = category?.subcategories.find((s) => s.slug === subSlug);

  const list = useMemo(() => {
    let arr = [...products];
    if (categorySlug) arr = arr.filter((p) => p.category === categorySlug);
    if (subSlug) arr = arr.filter((p) => p.subcategory === subSlug);
    if (query) {
      const q = query.toLowerCase();
      arr = arr.filter((p) => (p.name + " " + (p.nameEn || "")).toLowerCase().includes(q));
    }
    if (sort === "priceLow") arr.sort((a, b) => a.price - b.price);
    else if (sort === "priceHigh") arr.sort((a, b) => b.price - a.price);
    else arr.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    return arr;
  }, [products, categorySlug, subSlug, query, sort]);

  const title = query
    ? `"${query}"`
    : sub ? tr(sub.name) : category ? tr(category.name) : t("shop");

  return (
    <div>
      {/* Page header */}
      <div className="border-b border-neutral-200 bg-neutral-50">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 text-center">
          <div className="flex items-center justify-center gap-2 text-[11px] tracking-widest uppercase text-neutral-400 mb-4">
            <Link to="/" className="hover:text-[color:var(--brand)]">{t("home")}</Link>
            <span>/</span>
            {category ? <Link to={`/shop/${category.slug}`} className="hover:text-[color:var(--brand)]">{tr(category.name)}</Link> : <span>{t("shop")}</span>}
            {sub && <><span>/</span><span>{tr(sub.name)}</span></>}
          </div>
          <h1 className="brand-font text-4xl md:text-5xl tracking-wide text-neutral-900">{title}</h1>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10">
        {/* Subcategory chips */}
        {category?.subcategories?.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            <Link to={`/shop/${category.slug}`} className={`text-xs tracking-widest uppercase px-4 py-2 border transition-colors ${!subSlug ? "text-white border-transparent" : "text-neutral-600 border-neutral-300 hover:border-neutral-500"}`} style={!subSlug ? { backgroundColor: "var(--brand)" } : {}}>
              {t("allCategories")}
            </Link>
            {category.subcategories.map((s) => (
              <Link key={s.id} to={`/shop/${category.slug}/${s.slug}`} className={`text-xs tracking-widest uppercase px-4 py-2 border transition-colors ${subSlug === s.slug ? "text-white border-transparent" : "text-neutral-600 border-neutral-300 hover:border-neutral-500"}`} style={subSlug === s.slug ? { backgroundColor: "var(--brand)" } : {}}>
                {tr(s.name)}
              </Link>
            ))}
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center justify-between mb-8">
          <span className="text-xs tracking-widest uppercase text-neutral-500">{list.length} {lang === "tr" ? "ürün" : "products"}</span>
          <div className="w-52">
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="text-xs tracking-widest uppercase rounded-none border-neutral-300">
                <SelectValue placeholder={t("sortBy")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{t("newest")}</SelectItem>
                <SelectItem value="priceLow">{t("priceLow")}</SelectItem>
                <SelectItem value="priceHigh">{t("priceHigh")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {list.length === 0 ? (
          <div className="text-center py-24 text-neutral-400 tracking-wide">{t("noProducts")}</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6">
            {list.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
